// Context Readiness Manager - Centralized initialization coordination
import React, { 
  createContext, 
  useContext, 
  useReducer, 
  useCallback, 
  useEffect, 
  useMemo,
  ReactNode 
} from 'react';

// Context readiness state type
export interface ContextReadinessState {
  auth: boolean;
  theme: boolean;
  ui: boolean;
  data: boolean;
  project: boolean;
  scenario: boolean;
  navigation: boolean;
  notifications: boolean;
  errorBoundary: boolean;
  // Computed readiness states
  essential: boolean;      // Auth, Theme, UI must be ready
  core: boolean;          // Essential + Data + Project + Scenario ready
  complete: boolean;      // All contexts ready
}

// Context initialization callbacks
export interface ContextCallbacks {
  onAuthReady?: () => void;
  onThemeReady?: () => void;
  onUIReady?: () => void;
  onDataReady?: () => void;
  onProjectReady?: () => void;
  onScenarioReady?: () => void;
  onNavigationReady?: () => void;
  onNotificationsReady?: () => void;
  onEssentialReady?: () => void;
  onCoreReady?: () => void;
  onCompleteReady?: () => void;
}

// Context readiness manager type
export interface ContextReadinessManager {
  state: ContextReadinessState;
  
  // Context registration methods
  markContextReady: (context: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>) => void;
  markContextNotReady: (context: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>) => void;
  
  // Batch operations
  markMultipleReady: (contexts: (keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>)[]) => void;
  resetAllContexts: () => void;
  
  // Readiness checks
  isContextReady: (context: keyof ContextReadinessState) => boolean;
  isEssentialReady: () => boolean;
  isCoreReady: () => boolean;
  isCompleteReady: () => boolean;
  
  // Initialization sequence management
  getInitializationProgress: () => { ready: number; total: number; percentage: number };
  getNotReadyContexts: () => string[];
  
  // Callback management
  registerCallbacks: (callbacks: ContextCallbacks) => void;
  
  // Debug utilities
  getDebugInfo: () => {
    state: ContextReadinessState;
    timestamps: Record<string, number>;
    initializationSequence: string[];
  };
}

// Action types for state management
type ReadinessAction =
  | { type: 'MARK_READY'; context: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'> }
  | { type: 'MARK_NOT_READY'; context: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'> }
  | { type: 'MARK_MULTIPLE_READY'; contexts: (keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>)[] }
  | { type: 'RESET_ALL' };

// Initial state
const createInitialState = (): ContextReadinessState => ({
  auth: false,
  theme: false,
  ui: false,
  data: false,
  project: false,
  scenario: false,
  navigation: false,
  notifications: false,
  errorBoundary: false,
  essential: false,
  core: false,
  complete: false
});

// Compute derived states
const computeDerivedStates = (state: Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>): ContextReadinessState => {
  const essential = state.auth && state.theme && state.ui;
  const core = essential && state.data && state.project && state.scenario;
  const complete = core && state.navigation && state.notifications && state.errorBoundary;
  
  return {
    ...state,
    essential,
    core,
    complete
  };
};

// Reducer for context readiness state
const readinessReducer = (state: ContextReadinessState, action: ReadinessAction): ContextReadinessState => {
  switch (action.type) {
    case 'MARK_READY': {
      const newState = { ...state, [action.context]: true };
      return computeDerivedStates(newState);
    }
    
    case 'MARK_NOT_READY': {
      const newState = { ...state, [action.context]: false };
      return computeDerivedStates(newState);
    }
    
    case 'MARK_MULTIPLE_READY': {
      const newState = { ...state };
      action.contexts.forEach(context => {
        newState[context] = true;
      });
      return computeDerivedStates(newState);
    }
    
    case 'RESET_ALL': {
      return createInitialState();
    }
    
    default:
      return state;
  }
};

// Context creation
const ContextReadinessContext = createContext<ContextReadinessManager | null>(null);

// Hook to use context readiness manager
export const useContextReadiness = (): ContextReadinessManager => {
  const context = useContext(ContextReadinessContext);
  if (!context) {
    throw new Error('useContextReadiness must be used within a ContextReadinessProvider');
  }
  return context;
};

// Hook to wait for specific readiness levels
export const useWaitForReadiness = (level: 'essential' | 'core' | 'complete' = 'essential') => {
  const { state } = useContextReadiness();
  
  return useMemo(() => {
    switch (level) {
      case 'essential':
        return state.essential;
      case 'core':
        return state.core;
      case 'complete':
        return state.complete;
      default:
        return false;
    }
  }, [state, level]);
};

// Hook to register context readiness from individual providers
export const useRegisterContextReadiness = (
  contextName: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>,
  isReady: boolean
) => {
  const { markContextReady, markContextNotReady } = useContextReadiness();
  
  useEffect(() => {
    if (isReady) {
      markContextReady(contextName);
    } else {
      markContextNotReady(contextName);
    }
  }, [contextName, isReady, markContextReady, markContextNotReady]);
};

// Provider props
interface ContextReadinessProviderProps {
  children: ReactNode;
  callbacks?: ContextCallbacks;
  enableDebugLogging?: boolean;
}

// Context Readiness Provider
export const ContextReadinessProvider: React.FC<ContextReadinessProviderProps> = ({
  children,
  callbacks = {},
  enableDebugLogging = process.env.NODE_ENV === 'development'
}) => {
  const [state, dispatch] = useReducer(readinessReducer, createInitialState());
  
  // Track timestamps for debugging
  const [timestamps, setTimestamps] = React.useState<Record<string, number>>({});
  const [initializationSequence, setInitializationSequence] = React.useState<string[]>([]);
  
  // Mark context ready
  const markContextReady = useCallback((context: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>) => {
    dispatch({ type: 'MARK_READY', context });
    
    // Track timestamp
    setTimestamps(prev => ({ ...prev, [context]: Date.now() }));
    setInitializationSequence(prev => [...prev, `${context}:ready`]);
    
    if (enableDebugLogging) {
      console.log(`✅ [ContextReadiness] ${context} context ready`);
    }
  }, [enableDebugLogging]);
  
  // Mark context not ready
  const markContextNotReady = useCallback((context: keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>) => {
    dispatch({ type: 'MARK_NOT_READY', context });
    
    // Track timestamp
    setTimestamps(prev => ({ ...prev, [context]: Date.now() }));
    setInitializationSequence(prev => [...prev, `${context}:not-ready`]);
    
    if (enableDebugLogging) {
      console.log(`❌ [ContextReadiness] ${context} context not ready`);
    }
  }, [enableDebugLogging]);
  
  // Mark multiple contexts ready
  const markMultipleReady = useCallback((contexts: (keyof Omit<ContextReadinessState, 'essential' | 'core' | 'complete'>)[]) => {
    dispatch({ type: 'MARK_MULTIPLE_READY', contexts });
    
    // Track timestamps
    const now = Date.now();
    setTimestamps(prev => {
      const updated = { ...prev };
      contexts.forEach(context => {
        updated[context] = now;
      });
      return updated;
    });
    
    setInitializationSequence(prev => [...prev, ...contexts.map(c => `${c}:ready`)]);
    
    if (enableDebugLogging) {
      console.log(`✅ [ContextReadiness] Multiple contexts ready:`, contexts);
    }
  }, [enableDebugLogging]);
  
  // Reset all contexts
  const resetAllContexts = useCallback(() => {
    dispatch({ type: 'RESET_ALL' });
    setTimestamps({});
    setInitializationSequence(['reset']);
    
    if (enableDebugLogging) {
      console.log('🔄 [ContextReadiness] All contexts reset');
    }
  }, [enableDebugLogging]);
  
  // Readiness checks
  const isContextReady = useCallback((context: keyof ContextReadinessState) => {
    return state[context];
  }, [state]);
  
  const isEssentialReady = useCallback(() => state.essential, [state.essential]);
  const isCoreReady = useCallback(() => state.core, [state.core]);
  const isCompleteReady = useCallback(() => state.complete, [state.complete]);
  
  // Initialization progress
  const getInitializationProgress = useCallback(() => {
    const contexts = ['auth', 'theme', 'ui', 'data', 'navigation', 'notifications', 'errorBoundary'] as const;
    const ready = contexts.filter(context => state[context]).length;
    const total = contexts.length;
    const percentage = Math.round((ready / total) * 100);
    
    return { ready, total, percentage };
  }, [state]);
  
  // Get not ready contexts
  const getNotReadyContexts = useCallback(() => {
    const contexts = ['auth', 'theme', 'ui', 'data', 'navigation', 'notifications', 'errorBoundary'] as const;
    return contexts.filter(context => !state[context]);
  }, [state]);
  
  // Callback registration
  const registerCallbacks = useCallback((newCallbacks: ContextCallbacks) => {
    Object.assign(callbacks, newCallbacks);
  }, [callbacks]);
  
  // Debug info
  const getDebugInfo = useCallback(() => ({
    state,
    timestamps,
    initializationSequence
  }), [state, timestamps, initializationSequence]);
  
  // Handle readiness level callbacks
  useEffect(() => {
    if (state.essential && callbacks.onEssentialReady) {
      callbacks.onEssentialReady();
      if (enableDebugLogging) {
        console.log('🎯 [ContextReadiness] Essential contexts ready (Auth, Theme, UI)');
      }
    }
  }, [state.essential, callbacks, enableDebugLogging]);
  
  useEffect(() => {
    if (state.core && callbacks.onCoreReady) {
      callbacks.onCoreReady();
      if (enableDebugLogging) {
        console.log('🎯 [ContextReadiness] Core contexts ready (Essential + Data)');
      }
    }
  }, [state.core, callbacks, enableDebugLogging]);
  
  useEffect(() => {
    if (state.complete && callbacks.onCompleteReady) {
      callbacks.onCompleteReady();
      if (enableDebugLogging) {
        console.log('🎯 [ContextReadiness] All contexts ready - System fully initialized');
      }
    }
  }, [state.complete, callbacks, enableDebugLogging]);
  
  // Individual context callbacks
  useEffect(() => {
    if (state.auth && callbacks.onAuthReady) callbacks.onAuthReady();
  }, [state.auth, callbacks]);
  
  useEffect(() => {
    if (state.theme && callbacks.onThemeReady) callbacks.onThemeReady();
  }, [state.theme, callbacks]);
  
  useEffect(() => {
    if (state.ui && callbacks.onUIReady) callbacks.onUIReady();
  }, [state.ui, callbacks]);
  
  useEffect(() => {
    if (state.data && callbacks.onDataReady) callbacks.onDataReady();
  }, [state.data, callbacks]);
  
  useEffect(() => {
    if (state.navigation && callbacks.onNavigationReady) callbacks.onNavigationReady();
  }, [state.navigation, callbacks]);
  
  useEffect(() => {
    if (state.notifications && callbacks.onNotificationsReady) callbacks.onNotificationsReady();
  }, [state.notifications, callbacks]);
  
  // Debug logging for state changes
  useEffect(() => {
    if (enableDebugLogging) {
      const progress = getInitializationProgress();
      console.log(`📊 [ContextReadiness] Initialization progress: ${progress.percentage}% (${progress.ready}/${progress.total})`);
      
      if (progress.percentage < 100) {
        const notReady = getNotReadyContexts();
        console.log(`⏳ [ContextReadiness] Waiting for: ${notReady.join(', ')}`);
      }
    }
  }, [state, enableDebugLogging, getInitializationProgress, getNotReadyContexts]);
  
  // Create context value
  const contextValue: ContextReadinessManager = useMemo(() => ({
    state,
    markContextReady,
    markContextNotReady,
    markMultipleReady,
    resetAllContexts,
    isContextReady,
    isEssentialReady,
    isCoreReady,
    isCompleteReady,
    getInitializationProgress,
    getNotReadyContexts,
    registerCallbacks,
    getDebugInfo
  }), [
    state,
    markContextReady,
    markContextNotReady,
    markMultipleReady,
    resetAllContexts,
    isContextReady,
    isEssentialReady,
    isCoreReady,
    isCompleteReady,
    getInitializationProgress,
    getNotReadyContexts,
    registerCallbacks,
    getDebugInfo
  ]);
  
  return (
    <ContextReadinessContext.Provider value={contextValue}>
      {children}
    </ContextReadinessContext.Provider>
  );
};

// Readiness Display Component (for debugging)
export const ReadinessDisplay: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { state, getInitializationProgress, getNotReadyContexts } = useContextReadiness();
  const progress = getInitializationProgress();
  const notReady = getNotReadyContexts();
  
  if (compact) {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 10, 
        right: 10, 
        background: 'rgba(0,0,0,0.8)', 
        color: 'white', 
        padding: '8px 12px', 
        borderRadius: '4px', 
        fontSize: '12px',
        zIndex: 9999 
      }}>
        Init: {progress.percentage}%
        {notReady.length > 0 && (
          <div style={{ fontSize: '10px', opacity: 0.7 }}>
            Waiting: {notReady.join(', ')}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div style={{ 
      position: 'fixed', 
      bottom: 10, 
      left: 10, 
      background: 'rgba(0,0,0,0.9)', 
      color: 'white', 
      padding: '16px', 
      borderRadius: '8px', 
      fontSize: '14px',
      zIndex: 9999,
      maxWidth: '300px'
    }}>
      <h4 style={{ margin: '0 0 8px 0' }}>Context Readiness</h4>
      <div style={{ marginBottom: '8px' }}>Progress: {progress.percentage}% ({progress.ready}/{progress.total})</div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px', fontSize: '12px' }}>
        {Object.entries(state).map(([key, ready]) => (
          <React.Fragment key={key}>
            <span>{key}:</span>
            <span style={{ color: ready ? '#4ade80' : '#f87171' }}>
              {ready ? '✓' : '✗'}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ContextReadinessProvider;